export type SetupStage = "microphone" | "ice" | "signaling" | "ringing" | "talking";

// getUserMedia cannot be cancelled by the browser. Discard and stop a late stream,
// and reject the SDP operation immediately so SIP.js cannot send a late INVITE.
export class CallSetup {
  readonly controller = new AbortController();
  stage: SetupStage = "microphone";
  abort() { if (!this.controller.signal.aborted) this.controller.abort(); }
  check() { this.controller.signal.throwIfAborted(); }
  keep<T>(value: T, release: (value: T) => void): T {
    if (this.controller.signal.aborted) { release(value); this.check(); }
    this.controller.signal.addEventListener("abort", () => release(value), { once: true });
    return value;
  }
  run<T>(start: () => Promise<T>, discard?: (value: T) => void): Promise<T> {
    return new Promise((resolve, reject) => {
      const signal = this.controller.signal;
      if (signal.aborted) { reject(signal.reason); return; }
      const abort = () => reject(signal.reason);
      signal.addEventListener("abort", abort, { once: true });
      Promise.resolve().then(() => { this.check(); return start(); }).then((value) => {
        signal.removeEventListener("abort", abort);
        if (signal.aborted) { discard?.(value); reject(signal.reason); }
        else resolve(value);
      }, (error) => { signal.removeEventListener("abort", abort); reject(error); });
    });
  }
}

// Observe the current timer interval, not wall-clock changes or the total call.
export function setupTimerTiming(startedAt: number, timeoutBudgetMs: number, now = performance.now()) {
  const elapsedMs = Math.max(0, Math.round(now - startedAt));
  return { timeoutBudgetMs, elapsedMs, timerLagMs: Math.max(0, elapsedMs - timeoutBudgetMs) };
}

export function setupTimeoutMessage(stage: SetupStage): string {
  if (stage === "ringing") return "Абонент не ответил: превышено время ожидания ответа (90 секунд)";
  if (stage === "microphone") return "За 30 секунд не удалось получить микрофон. Проверьте разрешение и аудиоустройство";
  if (stage === "ice") return "За 30 секунд не удалось подготовить медиасоединение. Проверьте сеть, VPN и доступность TURN";
  return "За 30 секунд не получено подтверждение прохождения SIP-вызова";
}
